/**
 * 결과 화면 조회 묶음 기다리기(총괄 DEV-I-008). DOM 없이 단위 테스트한다.
 *
 * - 결과·문항·과제 요청을 함께 보내고, 요청 완료와 독립된 타이머(기본 30초)로 확인 제한을 둔다.
 * - 진행 중 어느 요청이든 401·403·404가 오면 나머지를 기다리지 않고 바로 끝낸다(인증·접근 오류 처리 우선).
 * - 끝난 묶음(시간 초과·조기 종료 포함)은 다시 결과를 내지 않는다. 그 뒤 도착한 응답은 버려진다.
 * - 끝날 때 아직 끝나지 않은 요청은 abort()로 취소를 요청한다(누적 방지). 취소는 브라우저 쪽 기다림을 끝낼 뿐,
 *   서버가 이미 처리했을 수 있다(결과 조회의 서버 제공 기록이 남았을 수 있음).
 */
import type { ApiResult } from './transport.js';

export type BatchSlot = 'grading' | 'items' | 'assignment';

export type BatchOutcome =
  /** 모든 요청이 끝남(성공·실패 무관). 판정은 result-model decideBatch */
  | { readonly kind: 'complete'; readonly grading: ApiResult; readonly items: ApiResult | null; readonly assignment: ApiResult | null }
  /** 401·403·404가 먼저 도착: 나머지는 기다리지 않음 */
  | { readonly kind: 'early'; readonly slot: BatchSlot; readonly result: Extract<ApiResult, { kind: 'http' }> }
  /** 확인 제한 시간이 지남: 끝나지 않은 요청 목록 */
  | { readonly kind: 'timeout'; readonly pending: readonly BatchSlot[] };

export interface BatchTimers {
  readonly setTimeout: (fn: () => void, ms: number) => unknown;
  readonly clearTimeout: (id: unknown) => void;
}
const realTimers: BatchTimers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (id) => globalThis.clearTimeout(id as ReturnType<typeof setTimeout>),
};

export const RESULT_CHECK_MS = 30_000;

const STOP = new Set([401, 403, 404]);

/**
 * requests: 이번에 보낸 요청(보내지 않은 것은 null). onSettle: 묶음이 끝나는 순간 한 번(취소 요청용).
 * 반환 Promise는 한 번만 결정된다. 결정 뒤 도착한 응답은 아무 것도 바꾸지 않는다.
 */
export function awaitResultBatch(
  requests: { readonly grading: Promise<ApiResult>; readonly items: Promise<ApiResult> | null; readonly assignment: Promise<ApiResult> | null },
  opts: { readonly timeoutMs?: number; readonly timers?: BatchTimers; readonly onSettle?: (o: BatchOutcome) => void } = {},
): Promise<BatchOutcome> {
  const timers = opts.timers ?? realTimers;
  return new Promise<BatchOutcome>((resolve) => {
    let done = false;
    const got: Partial<Record<BatchSlot, ApiResult>> = {};
    const slots = (Object.keys(requests) as BatchSlot[]).filter((s) => requests[s] !== null);
    const finish = (o: BatchOutcome) => {
      if (done) return;
      done = true;
      timers.clearTimeout(timer);
      opts.onSettle?.(o);
      resolve(o);
    };
    const timer = timers.setTimeout(() => finish({ kind: 'timeout', pending: slots.filter((s) => got[s] === undefined) }), opts.timeoutMs ?? RESULT_CHECK_MS);
    for (const s of slots) {
      requests[s]!.then((r) => {
        if (done) return; // 끝난 묶음: 늦은 응답은 버림
        got[s] = r;
        if (r.kind === 'http' && STOP.has(r.status)) { finish({ kind: 'early', slot: s, result: r }); return; }
        if (slots.every((x) => got[x] !== undefined)) {
          finish({ kind: 'complete', grading: got.grading!, items: got.items ?? null, assignment: got.assignment ?? null });
        }
      }, () => {
        // transport는 거부하지 않지만, 혹시 거부되면 통신 실패로 본다
        if (done) return;
        got[s] = { kind: 'network', gen: -1 };
        if (slots.every((x) => got[x] !== undefined)) finish({ kind: 'complete', grading: got.grading!, items: got.items ?? null, assignment: got.assignment ?? null });
      });
    }
  });
}
